import { useCallback, useRef, useState } from "react";
import { type AuthContextProps, useAuth } from "react-oidc-context";
import { AUTH_FLOW_TOKEN_EXPIRATION_BUFFER_MS } from "@/lib/authSessionConfig";
import { PAGE_PATHS } from "@/config/urls";
import { useHumhubSessionLogin } from "@/hooks/useHumhubSessionLogin";
import { useOidcTokenRefresh } from "@/hooks/useOidcTokenRefresh";
import { getLatestOidcToken } from "@/lib/authSession";
import {
  buildAuthSessionSnapshot,
  getOidcTokenCandidates,
} from "@/lib/authSessionTiming";
import { saveOriginalUrl } from "@/lib/TokenManager";

export const useAuthFlow = () => {
  const [isAuthenticating, setIsAuthenticating] = useState(false);
  const auth = useAuth();
  const { refreshAccessToken } = useOidcTokenRefresh();
  const { loginToHumhubSession } = useHumhubSessionLogin();
  const authPromiseRef = useRef<Promise<{
    success: boolean;
    error?: string;
  }> | null>(null);

  const isTokenExpired = useCallback((expiresAt?: number) => {
    if (!expiresAt) {
      return false;
    }

    return Date.now() >= expiresAt - AUTH_FLOW_TOKEN_EXPIRATION_BUFFER_MS;
  }, []);

  const tryHumhubLogin = useCallback(
    async (
      tokenInfo: { token: string; expiresAt?: number } | null | undefined,
    ) => {
      if (!tokenInfo?.token || isTokenExpired(tokenInfo.expiresAt)) {
        return false;
      }

      try {
        const result = await loginToHumhubSession(
          tokenInfo.token,
          tokenInfo.expiresAt,
        );
        if (result?.auth_token) {
          return true;
        }
      } catch (_error) {
        // ログ出力はログインフック側で行う
      }

      return false;
    },
    [isTokenExpired, loginToHumhubSession],
  );

  /**
   * OIDC認証開始（コールバックURL検出対応）
   */
  const startOidcAuth = useCallback((auth: AuthContextProps) => {
    // 現在のURLが既にコールバックURLの場合は保存しない
    const currentUrl = window.location.href;
    if (!currentUrl.includes(PAGE_PATHS.CALLBACK)) {
      saveOriginalUrl(currentUrl);
    }

    // OIDC認証リダイレクト
    auth.signinRedirect();
  }, []);

  /**
   * 統合認証フロー実行
   */
  const executeAuthFlow = useCallback(async (): Promise<{
    success: boolean;
    error?: string;
  }> => {
    if (authPromiseRef.current) {
      return authPromiseRef.current;
    }

    let startedRedirect = false;
    const authPromise = (async () => {
      setIsAuthenticating(true);

      try {
        const tokenCandidates = getOidcTokenCandidates(
          buildAuthSessionSnapshot(auth),
          getLatestOidcToken(),
        );

        for (const candidate of tokenCandidates) {
          if (await tryHumhubLogin(candidate)) {
            return { success: true };
          }
        }

        const refreshedToken = await refreshAccessToken();
        if (refreshedToken && (await tryHumhubLogin(refreshedToken))) {
          return { success: true };
        }

        // refreshトークンが無効でリダイレクトする場合、再度の多重リダイレクトを防ぐ
        startedRedirect = true;
        startOidcAuth(auth);
        return { success: false, error: "Redirecting to authentication" };
      } catch (error) {
        console.error("❌ Auth flow error:", error);
        return {
          success: false,
          error: error instanceof Error ? error.message : "Unknown error",
        };
      } finally {
        setIsAuthenticating(false);
        // リダイレクトを開始した場合は、新しい認証フロー開始を抑止するためにPromiseを維持する
        if (!startedRedirect) {
          authPromiseRef.current = null;
        }
      }
    })();

    authPromiseRef.current = authPromise;
    return authPromise;
  }, [auth, refreshAccessToken, startOidcAuth, tryHumhubLogin]);

  return {
    executeAuthFlow,
    isAuthenticating,
  };
};
